import React from 'react';

import Icon from 'core/modules/atoms/Icon';

import './_stat-card.scss';

type StatCardIconVariant = 'mastery' | 'rollback' | 'success' | 'time' | 'timeline';

type StatCardLayout = 'inline' | 'spread' | 'stacked';

type StatCardProps = {
  ariaLabel?: string;
  children?: React.ReactNode;
  description?: React.ReactNode;
  iconType: string;
  iconVariant: StatCardIconVariant;
  isExpandable?: boolean;
  isExpanded?: boolean;
  label: string;
  layout?: StatCardLayout;
  onToggle?: () => void;
  value: React.ReactNode;
};

/**
 * StatCard - Reusable stat card displaying an icon, a value and a label.
 * - When `isExpandable`, a toggle button reveals `children` below.
 * - When NOT expandable, `children` is always rendered below.
 * - `description` is always rendered below the row when provided.
 */
function StatCard({
  ariaLabel,
  children,
  description,
  iconType,
  iconVariant,
  isExpandable = false,
  isExpanded = false,
  label,
  layout = 'inline',
  onToggle,
  value,
}: Readonly<StatCardProps>): JSX.Element {
  const hasBody = description || children;
  const cardClassName = `stat-card stat-card--layout-${layout}${isExpandable || hasBody ? ' stat-card--expandable' : ''}`;
  const toggleClassName = `stat-card__toggle${isExpanded ? ' stat-card__toggle--expanded' : ''}`;
  const shouldRenderChildren = children && (!isExpandable || isExpanded);

  return (
    <div className={cardClassName}>
      <div className="stat-card__row">
        <div className={`stat-card__icon stat-card__icon--${iconVariant}`}>
          <Icon size="default" type={iconType} />
        </div>
        <div className="stat-card__info">
          <span className="stat-card__label">{label}</span>
          <span className="stat-card__value">{value}</span>
        </div>
        {isExpandable && (
          <button
            aria-expanded={isExpanded}
            aria-label={ariaLabel ?? label}
            className={toggleClassName}
            onClick={onToggle}
            type="button"
          >
            <Icon color="grey-03" size="default" type="arrow-down" />
          </button>
        )}
      </div>
      {description && <p className="stat-card__description">{description}</p>}
      {shouldRenderChildren && children}
    </div>
  );
}

export default StatCard;
